// t() reads the page language, which the compiler cannot see, and this stays
// on screen while the language changes.
"use no memo";
import { useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import Check from "~icons/ph/check";
import PhoneOff from "~icons/ph/phone-slash";
import WarningCircle from "~icons/ph/warning-circle";
import X from "~icons/ph/x";
import { t } from "../i18n";
import { useTopModal } from "./components";

type Tone = "success" | "alert" | "missed";
let toast: { message: string; tone: Tone } | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
// What a person must not miss by looking away stays until closed, and waits its
// turn, so a later message cannot erase it.
const lasting: string[] = [];
const listeners = new Set<() => void>();

function show(next: typeof toast) {
  toast = next;
  listeners.forEach((listener) => listener());
}

function showLasting() {
  if (timer) clearTimeout(timer);
  show(lasting[0] ? { message: lasting[0], tone: "alert" } : null);
}

export function notify(message: string, tone: Tone = "alert") {
  show({ message: t(message), tone });
  if (timer) clearTimeout(timer);
  timer = setTimeout(showLasting, 4500);
}

export function notifyUntilClosed(message: string) {
  const text = t(message);
  if (!lasting.includes(text)) lasting.push(text);
  showLasting();
}

// These speak of one account, and the next person here is not its owner.
export function forgetLasting() {
  if (toast?.message === lasting[0]) show(null);
  lasting.length = 0;
}

function close() {
  if (toast?.message === lasting[0]) lasting.shift();
  showLasting();
}

export function Toast() {
  const current = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
    () => toast,
  );
  // The toast joins an open modal, the only part of the page that is not inert.
  const topModal = useTopModal();
  if (!current) return null;
  const notice = (
    <div
      // A new live region per tone so alerts are announced as alerts.
      key={current.tone}
      className={`toast ${current.tone}`}
      role={current.tone === "alert" ? "alert" : "status"}
    >
      {current.tone === "alert" ? (
        <WarningCircle width={16} height={16} />
      ) : current.tone === "missed" ? (
        <PhoneOff width={16} height={16} />
      ) : (
        <Check width={16} height={16} />
      )}
      <span>{current.message}</span>
      <button
        className="icon-button"
        onClick={close}
        aria-label={t("إغلاق التنبيه")}
      >
        <X width={15} height={15} />
      </button>
    </div>
  );
  return topModal ? createPortal(notice, topModal) : notice;
}
